
import type { TimelyQueriesResponse } from '@empathybroker/tsclient-stats/dist/models/timely-queries/timely-queries-response'
import type { TimelyQueriesDistributionData } from '@empathybroker/tsclient-stats/dist/models/timely-queries/timely-queries-response-data-period'
import type { TimelySearchDistributionData } from '@empathybroker/tsclient-stats/dist/models/timely-search/timely-search-distribution-data'
import type { TimelySearchResponse } from '@empathybroker/tsclient-stats/dist/models/timely-search/timely-search-response'
import type {
  EvolutionChartConfig,
  EvolutionChartData,
  Params,
  ValueConfig,
} from '@empathyco/insights'
import type { PropType } from 'vue'
import type { KpisOptionComposable } from '../../../composables/use-kpis'
import type { CompareFilterEnum } from '../../../enums'
import type { PeriodsData, RegularFilter } from '../../../models'
import { EvolutionChartComponent } from '@empathyco/insights'
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import { FilterIdEnum, KpiEnum } from '../../../enums'
import { ANALYTICS_CONSTS } from '../../../store/analytics/analytics.consts'
import { CONTEXT_CONSTS } from '../../../store/context/context.consts'
import KpisLegend from '../../kpis-legend.component.vue'
import { EpbSwitch } from '../../shared/epb-switch'

type TimelyResponse = TimelySearchResponse | TimelyQueriesResponse
type TimelyDistributionData = TimelySearchDistributionData | TimelyQueriesDistributionData

export default defineComponent({
  name: 'DistributionChart',
  components: {
    EpbSwitch,
    KpisLegend,
  },
  props: {
    selectedId: {
      type: String,
      required: false,
      default: undefined,
    },
    kpiOptions: {
      type: Array as PropType<Array<KpisOptionComposable>>,
      required: true,
    },
    size: {
      type: String,
      default: () => 'normal',
    },
  },
  data() {
    return {
      showPercentage: false,
      selectedKpis: [],
      component: undefined,
      messages: this.$epb.messages.insights.analytics,
    }
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      filters: CONTEXT_CONSTS.getters.enabledFilters,
    }),
    ...mapGetters(ANALYTICS_CONSTS.moduleName, {
      data: ANALYTICS_CONSTS.getters.data,
    }),
    compareFilterValue(): CompareFilterEnum {
      let compareFilterValue: CompareFilterEnum
      const compareFilter: RegularFilter = this.filters[FilterIdEnum.COMPARE]
      if (compareFilter) {
        compareFilterValue = compareFilter.selectedValues[0] as CompareFilterEnum
      }
      return compareFilterValue
    },
    kpisItems(): ValueConfig[] {
      return this.kpiOptions
        .filter((option: KpisOptionComposable) => {
          return !this.showPercentage || option.id !== KpiEnum.QUERIES
        })
        .map((option: KpisOptionComposable) => ({
          ...option,
          unfilled: true,
        }))
    },
    areaChartParams(): Params<EvolutionChartConfig, EvolutionChartData> {
      return {
        config: {
          filteredValues: this.kpisItems.filter(item => {
            return this.selectedKpis.includes(item.id)
          }),
          maxValue: this.showPercentage ? 100 : undefined,
          valueUnits: this.showPercentage ? '%' : undefined,
        },
        data: this.areaChartData,
      }
    },
    areaChartData(): EvolutionChartData[] {
      let distData: PeriodsData<TimelyResponse>
      if (!this.selectedId) {
        distData = this.data.overall.dist
      } else {
        const selectedIdData = this.data.ids[this.selectedId]
        if (selectedIdData) {
          distData = selectedIdData.dist
        }
      }
      let data = []
      if (!!distData && !!distData.currentPeriod) {
        const distributionData = distData.currentPeriod.data.distributionData
        let distributionDataToCompare: (
          | TimelySearchDistributionData
          | TimelyQueriesDistributionData
        )[]
        if (!!this.compareFilterValue && !!distData[this.compareFilterValue]) {
          distributionDataToCompare = distData[this.compareFilterValue].data.distributionData
        }
        let isHourlyDist = false
        if (distributionData.length > 1) {
          isHourlyDist =
            distributionData[1].datetime.getTime() - distributionData[0].datetime.getTime() <
            1000 * 60 * 60 * 12
        }
        data = distributionData.map(
          (distributionDataItem: TimelyDistributionData, index: number) => {
            const distributionDataItemToCompare = distributionDataToCompare
              ? distributionDataToCompare[index]
              : undefined
            return this.getAreaChartDataItem(
              distributionDataItem,
              distributionDataItemToCompare,
              isHourlyDist,
            )
          },
        )
      }
      return data
    },
  },
  watch: {
    showPercentage(showPercentage: boolean): void {
      if (showPercentage) {
        if (this.isKpiSelected(KpiEnum.QUERIES)) {
          if (this.selectedKpis.length > 1) {
            this.unselectKpi(KpiEnum.QUERIES)
          } else {
            this.resetSelectedKpis()
          }
        }
      } else {
        this.resetSelectedKpis()
      }
    },
    compareFilterValue(compareFilterValue: CompareFilterEnum): void {
      if (compareFilterValue) {
        this.unselectKpiByIndex(0)
      }
    },
    areaChartParams: {
      handler(params: Params<EvolutionChartConfig, EvolutionChartData>): void {
        this.component.update(params)
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new EvolutionChartComponent()
  },
  mounted(): void {
    this.resetSelectedKpis()
    this.component.init(this.$refs.component, this.areaChartParams)
  },
  beforeDestroy(): void {
    this.component.destroy()
  },
  methods: {
    resetSelectedKpis(): void {
      this.selectedKpis = this.kpisItems.map(kpiLegendItem => kpiLegendItem.id)
      if (this.compareFilterValue) {
        this.unselectKpiByIndex(0)
      }
    },
    unselectKpi(kpi: KpiEnum): void {
      const index = this.selectedKpis.indexOf(kpi)
      this.unselectKpiByIndex(index)
    },
    unselectKpiByIndex(index: number): void {
      this.selectedKpis.splice(index, 1)
    },
    isKpiSelected(kpi: KpiEnum): boolean {
      return this.selectedKpis.includes(kpi)
    },
    getAreaChartDataItem(
      distributionDataItem: TimelyDistributionData,
      distributionDataItemToCompare?: TimelySearchDistributionData | TimelyQueriesDistributionData,
      isHourlyDist = false,
    ) {
      return {
        id: distributionDataItem.datetime.toISOString(),
        labels: this.getAreaChartDataItemLabels(
          distributionDataItem,
          distributionDataItemToCompare,
          isHourlyDist,
        ),
        values: this.getAreaChartDataItemValues(
          distributionDataItem,
          distributionDataItemToCompare,
        ),
      }
    },
    getAreaChartDataItemLabels(
      distributionDataItem: TimelyDistributionData,
      distributionDataItemToCompare: TimelyDistributionData,
      isHourlyDist: boolean,
    ): string[] {
      const dateLocaleOptions = isHourlyDist
        ? {
            hour: '2-digit',
            minute: '2-digit',
          }
        : {
            month: 'short',
            day: 'numeric',
          }
      const dateWithoutOffset = this.getDateWithoutTimezone(distributionDataItem.datetime)
      const labels = [dateWithoutOffset.toLocaleString(this.$epb.locale, dateLocaleOptions)]
      if (distributionDataItemToCompare) {
        const dateToCompareWithoutOffset = this.getDateWithoutTimezone(
          distributionDataItemToCompare.datetime,
        )
        const localeString: string = dateToCompareWithoutOffset.toLocaleString(
          this.$epb.locale,
          dateLocaleOptions,
        )
        labels.push(`${localeString} ${this.compareFilterValue}`)
      }
      return labels
    },
    getDateWithoutTimezone(date: Date): Date {
      return new Date(date.getTime() + date.getTimezoneOffset() * (60 * 1000))
    },
    getAreaChartDataItemValues(
      distributionDataItem: TimelyDistributionData,
      distributionDataItemToCompare: TimelyDistributionData,
    ): Partial<Record<KpiEnum, [number] | [number, number]>> {
      const values: Partial<Record<KpiEnum, [number] | [number, number]>> = {}

      this.kpiOptions.forEach(({ id }: KpisOptionComposable) => {
        values[id] = this.getKpiValues(distributionDataItem, distributionDataItemToCompare, id)
      })

      return values
    },
    getKpiValues(
      distributionData: TimelyDistributionData,
      distributionDataToCompare: TimelyDistributionData,
      kpi: KpiEnum,
    ): [number] | [number, number] {
      const getKpiValueFn = this.getKpiValueFn(kpi, this.showPercentage)
      const kpiValue = getKpiValueFn(distributionData)
      const kpiValueToCompare = distributionDataToCompare
        ? getKpiValueFn(distributionDataToCompare)
        : undefined

      return kpiValueToCompare !== undefined ? [kpiValue, kpiValueToCompare] : [kpiValue]
    },
    getKpiValueFn(kpi: KpiEnum, usePercentage: boolean): (data: TimelyDistributionData) => number {
      return {
        [KpiEnum.QUERIES]: (data: TimelyDistributionData) =>
          usePercentage ? data.queryCountPct : data.queryCount,
        [KpiEnum.CLICKS]: (data: TimelyDistributionData) =>
          usePercentage ? data.clickCountPct : data.clickCount,
        [KpiEnum.FINDABILITY]: (data: TimelyDistributionData) =>
          usePercentage ? data.successCountPct : data.successCount,
        [KpiEnum.ADD2CART]: (data: TimelyDistributionData) =>
          usePercentage ? data.add2cartCountPct : data.add2cartCount,
        [KpiEnum.CHECKOUT]: (data: TimelyDistributionData) =>
          usePercentage ? data.checkoutCountPct : data.checkoutCount,
        [KpiEnum.NO_RESULTS]: (data: TimelyDistributionData) =>
          usePercentage ? data.noResultsCountPct : data.noResultsCount,
      }[kpi]
    },
  },
})
